/**
 * Data-field configuration: the pure logic behind the top-right readout boxes.
 *
 * Two jobs live here, both kept free of React and zustand so they can be unit
 * tested on their own:
 *   1. Which fields are valid, the default set, and how to clean a saved list
 *      (drop anything we no longer recognise, cap the count) so an older or
 *      corrupted profile can never crash the strip.
 *   2. Which single field is the PRIMARY one right now — the big number the
 *      skipper should find first — given what we're doing (underway, following a
 *      route, at anchor) or an explicit pin the user set.
 *
 * The shell store owns the state and the localStorage read/write; it leans on
 * these helpers so the rules live in one testable place.
 */
import type { DataField } from './shell';

/** Every field the strip knows how to draw. Anything outside this is dropped. */
export const ALL_DATA_FIELDS: readonly DataField[] = [
  'sog',
  'cog',
  'heading',
  'pos',
  'xte',
  'brgwp',
  'dtwp',
  'eta',
  'gnss',
  'uke',
  'trip',
  'tide',
  'cpa',
];

/**
 * The out-of-the-box set. SOG is the number underway, COG backs it up, and GNSS
 * accuracy is the honest "how much should I trust the fix" reading — a sensible
 * spread that shows the two-tier hierarchy straight away. A user who customises
 * overrides this and their choice is what persists.
 */
export const DEFAULT_DATA_FIELDS: readonly DataField[] = ['sog', 'cog', 'gnss'];

/**
 * Most boxes we'll draw before it starts crowding the chart. The stack runs down
 * the top-right, so past this it's fighting for space with the chart itself.
 */
export const MAX_DATA_FIELDS = 5;

/** localStorage keys, versioned so a future shape change can migrate cleanly. */
export const DATA_FIELDS_KEY = 'acp.dataFields.v1';
export const PRIMARY_PIN_KEY = 'acp.dataFieldPrimary.v1';

const VALID = new Set<string>(ALL_DATA_FIELDS);

/**
 * Clean a raw (possibly saved, possibly rubbish) list into a list we can draw:
 * keep only fields we still recognise, cap the length, and fall back to the
 * default set if that leaves us with nothing. Duplicates are allowed through on
 * purpose — the picker has always let a skipper show the same field twice.
 */
export function sanitiseDataFields(raw: unknown): DataField[] {
  if (!Array.isArray(raw)) return [...DEFAULT_DATA_FIELDS];
  const cleaned = raw.filter((x): x is DataField => typeof x === 'string' && VALID.has(x));
  const capped = cleaned.slice(0, MAX_DATA_FIELDS);
  return capped.length ? capped : [...DEFAULT_DATA_FIELDS];
}

/** Clean a saved primary pin: a known field, or null when unset/unknown. */
export function sanitisePrimaryPin(raw: unknown): DataField | null {
  return typeof raw === 'string' && VALID.has(raw) ? (raw as DataField) : null;
}

/**
 * What the boat is doing, in the order that decides the primary. Anchored wins
 * because a dragging anchor is the thing you most want to catch; then following
 * a route (the cross-track number is the live steering cue); then simply making
 * way; then sitting idle.
 */
export type NavContext = 'anchored' | 'following' | 'underway' | 'idle';

export function navContext(o: {
  anchored: boolean;
  following: boolean;
  sog: number;
}): NavContext {
  if (o.anchored) return 'anchored';
  if (o.following) return 'following';
  if (o.sog > 0) return 'underway';
  return 'idle';
}

/**
 * For each context, the fields worth promoting to primary, best first. We can
 * only promote a field that's actually on screen, so this is a preference list,
 * not a guarantee — the first one present wins.
 *
 * At anchor there's no dedicated swing/range box in the strip (that lives in the
 * anchor-watch overlay and its alarm), so we lean on the next best things the
 * strip does carry: depth under the keel and how tight the GPS fix is, both of
 * which tell you whether you're sitting safely.
 */
const CONTEXT_PRIORITY: Record<NavContext, readonly DataField[]> = {
  anchored: ['uke', 'gnss', 'pos'],
  following: ['xte', 'dtwp', 'brgwp', 'eta'],
  underway: ['sog', 'cog'],
  idle: ['sog', 'pos'],
};

/**
 * Pick the one primary field from those on screen. An explicit pin wins outright
 * as long as that field is still shown; otherwise we take the first
 * context-preferred field that's present, and failing that just the first box so
 * there's always a primary.
 */
export function selectPrimaryField(
  fields: readonly DataField[],
  ctx: NavContext,
  pinned: DataField | null,
): DataField {
  if (fields.length === 0) return DEFAULT_DATA_FIELDS[0];
  if (pinned && fields.includes(pinned)) return pinned;
  for (const pref of CONTEXT_PRIORITY[ctx]) {
    if (fields.includes(pref)) return pref;
  }
  return fields[0];
}

/**
 * Index of the primary box, so the renderer knows which one to enlarge. Returns
 * the first match (a field can appear twice) and never -1, since selectPrimary
 * only ever returns a field that's present, or fields[0].
 */
export function primaryIndex(
  fields: readonly DataField[],
  ctx: NavContext,
  pinned: DataField | null,
): number {
  const primary = selectPrimaryField(fields, ctx, pinned);
  const i = fields.indexOf(primary);
  return i < 0 ? 0 : i;
}
